import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { 
    Loader2, 
    Send,
    DollarSign,
    ThumbsUp,
    Clock,
    AlertTriangle,
    XCircle,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Ultra Quick Review Form
 * Designed for 10-second review submission
 * Uses tap-friendly selection buttons only
 */

// Payment Selection Options - Ordered from worst to best (left to right)
const PAYMENT_OPTIONS = [
    { id: 'problem', label: 'Problem', icon: XCircle, color: 'red', value: { payment_received: false, payment_timeliness: 1 } },
    { id: 'late', label: 'Late', icon: Clock, color: 'amber', value: { payment_received: true, payment_timeliness: 2 } },
    { id: 'on_time', label: 'On Time', icon: DollarSign, color: 'emerald', value: { payment_received: true, payment_timeliness: 5 } }
];

// Experience Selection Options - Ordered from worst to best (left to right)
const EXPERIENCE_OPTIONS = [
    { id: 'major_issues', label: 'More Issues', rating: 1, color: 'red' },
    { id: 'difficult', label: 'Difficult', rating: 2, color: 'orange' },
    { id: 'okay', label: 'Okay', rating: 3, color: 'amber' },
    { id: 'good', label: 'Good', rating: 4, color: 'emerald' },
    { id: 'great', label: 'Great', rating: 5, color: 'emerald' }
];

// Selection Button Component - Min 44px tap target for mobile
const SelectionButton = ({ selected, onClick, children, color = 'primary', testId }) => {
    const colorStyles = {
        emerald: selected ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-background hover:bg-emerald-500/10 border-border hover:border-emerald-500/50',
        amber: selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-background hover:bg-amber-500/10 border-border hover:border-amber-500/50',
        orange: selected ? 'bg-orange-500 text-white border-orange-500' : 'bg-background hover:bg-orange-500/10 border-border hover:border-orange-500/50',
        red: selected ? 'bg-red-500 text-white border-red-500' : 'bg-background hover:bg-red-500/10 border-border hover:border-red-500/50',
        primary: selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-primary/10 border-border hover:border-primary/50'
    };

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "flex-1 min-h-[56px] min-w-[44px] px-3 py-3 rounded-xl border-2 font-semibold transition-all",
                "active:scale-95 touch-manipulation",
                colorStyles[color]
            )}
            data-testid={testId}
        >
            {children}
        </button>
    );
};

const QuickReviewForm = ({ 
    addressId, 
    addressDisplay, 
    onSubmit, 
    loading = false,
    jobType = 'General Contracting',
    projectSize = 'Medium'
}) => {
    const [payment, setPayment] = useState(null);
    const [experience, setExperience] = useState(null);
    const [notes, setNotes] = useState('');
    const [showNotes, setShowNotes] = useState(false);

    const canSubmit = payment !== null && experience !== null;

    const handleSubmit = () => {
        if (!canSubmit) return;

        const paymentData = PAYMENT_OPTIONS.find(p => p.id === payment)?.value || {};
        const experienceData = EXPERIENCE_OPTIONS.find(e => e.id === experience);
        
        // Get today's date in ISO format
        const today = new Date().toISOString().split('T')[0];
        
        // Calculate rating value from experience (1-5 scale)
        const ratingValue = experienceData?.rating || 3;
        
        const reviewData = {
            address_id: addressId,
            job_type: jobType,
            project_size: projectSize,
            // Required fields for Quick Review - auto-filled with sensible defaults
            job_start_date: today,
            job_completion_date: today,
            contract_signed: true, // Assume contract was signed for Quick Review
            job_confirmation: true, // User is confirming they did the work by submitting
            overall_rating: ratingValue,
            payment_received: paymentData.payment_received ?? true,
            would_work_again: ratingValue >= 3,
            require_deposit_next_time: paymentData.payment_received === false,
            decline_future_work: ratingValue <= 2,
            ratings: {
                // All required fields from ReviewRatings model
                communication: ratingValue,
                payment_timeliness: paymentData.payment_timeliness || 3,
                schedule_reliability: ratingValue,
                change_order_fairness: ratingValue,
                jobsite_access: ratingValue,
                professionalism: ratingValue
            },
            notes: notes.trim() || '',
            context: null // Optional ReviewContext object
        };

        onSubmit(reviewData);
    };

    return (
        <div className="space-y-6">
            {/* Address Display */}
            {addressDisplay && (
                <div className="text-center mb-2">
                    <p className="text-sm text-muted-foreground">Reviewing</p>
                    <p className="font-semibold text-foreground">{addressDisplay}</p>
                </div>
            )}

            {/* Disclaimer Banner */}
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg" data-testid="review-disclaimer-banner">
                <p className="text-xs text-muted-foreground text-center leading-relaxed">
                    Your ratings represent your personal perception and opinion of your job experience. 
                    Do not include factual claims, names, or identifying information about any individual.
                </p>
            </div>

            {/* PAYMENT SECTION */}
            <div>
                <h3 className="font-semibold text-foreground mb-1 text-center">
                    My payment experience
                </h3>
                <p className="text-xs text-muted-foreground text-center mb-3">
                    Worst → Best
                </p>
                <div className="flex gap-2">
                    {PAYMENT_OPTIONS.map((option) => (
                        <SelectionButton
                            key={option.id}
                            selected={payment === option.id}
                            onClick={() => setPayment(option.id)}
                            color={option.color}
                            testId={`payment-${option.id}`}
                        >
                            <div className="flex flex-col items-center gap-1">
                                <option.icon className="h-5 w-5" />
                                <span className="text-sm">{option.label}</span>
                            </div>
                        </SelectionButton>
                    ))}
                </div>
            </div>

            {/* EXPERIENCE SECTION */}
            <div>
                <h3 className="font-semibold text-foreground mb-1 text-center">
                    My overall experience
                </h3>
                <p className="text-xs text-muted-foreground text-center mb-3">
                    Worst → Best
                </p>
                <div className="grid grid-cols-5 gap-2">
                    {EXPERIENCE_OPTIONS.map((option) => (
                        <SelectionButton
                            key={option.id}
                            selected={experience === option.id}
                            onClick={() => setExperience(option.id)}
                            color={option.color}
                            testId={`experience-${option.id}`}
                        >
                            <span className="text-xs sm:text-sm">{option.label}</span>
                        </SelectionButton>
                    ))}
                </div>
            </div>

            {/* OPTIONAL NOTES */}
            <div>
                <button
                    type="button"
                    onClick={() => setShowNotes(!showNotes)}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full justify-center py-2"
                >
                    {showNotes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showNotes ? 'Hide notes' : 'Add notes (optional)'}
                </button>
                
                {showNotes && (
                    <>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any additional details..."
                            className="mt-2 min-h-[80px] bg-accent border-border"
                            maxLength={250}
                            data-testid="quick-notes-input"
                        />
                        {/* Evidence Warning for long notes */}
                        {notes.length > 150 && (
                            <div className="mt-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-600 dark:text-amber-400">
                                    Consider uploading evidence to strengthen your review credibility.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* SUBMIT BUTTON */}
            <Button
                onClick={handleSubmit}
                disabled={!canSubmit || loading}
                className="w-full h-14 text-lg font-semibold"
                data-testid="quick-submit-btn"
            >
                {loading ? (
                    <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                    </>
                ) : (
                    <>
                        <Send className="mr-2 h-5 w-5" />
                        Submit Review
                    </>
                )}
            </Button>

            {/* Help Text */}
            {!canSubmit && (
                <p className="text-center text-sm text-muted-foreground">
                    Select payment status and experience to submit
                </p>
            )}
        </div>
    );
};

export default QuickReviewForm;
